import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { shallow } from "zustand/shallow";

import { LIVE_EDGE_THRESHOLD_IN_SECONDS } from "../../constants";
import * as Constants from "../../constants";
import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { UI_SIZE } from "../../enums";
import { secondsToHHMMSSFormat } from "../../helpers/timeHelpers";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { ControlBarButton } from "../../styles/commonStyles";
import {
  controlsTextColor,
  progressBarBufferColor,
  progressBarElapsedColor,
  progressBarLiveElapsedColor
} from "../../styles/variables";
import { SeekTooltip } from "../SeekTooltip";
import {
  BufferProgress,
  ElapsedProgress,
  HoverProgress,
  Progress,
  SeekSlider,
  Wrapper
} from "../share/ProgressBar";
import { getTimelineAriaValueText } from "../Timeline/timeline-helpers";

const resolveDvrWindow = dvrWindow =>
  dvrWindow ? dvrWindow - Constants.DVR_WINDOW_OFFSET_IN_SECONDS : undefined;

const TimelineContainer = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  justify-content: space-between;

  > *:first-child {
    margin-left: 4px;
  }

  > *:last-child {
    margin-right: 4px;
  }
`;

const Time = styled.time<{ uiSize: UI_SIZE }>`
  font-size: ${({ uiSize }) => (uiSize === UI_SIZE.LARGE ? "14px" : "12px")};
  font-weight: bold;
  line-height: 32px;
  min-width: 30px;
  word-break: normal;
`;

const LiveButton = styled(ControlBarButton)<{
  isLiveEdge: boolean;
  uiSize: UI_SIZE;
}>`
  background: ${props =>
    props.isLiveEdge ? progressBarLiveElapsedColor : progressBarBufferColor};
  border-radius: 4px;
  color: ${controlsTextColor};
  font-size: ${props => (props.uiSize === UI_SIZE.LARGE ? "14px" : "12px")};
  font-weight: bold;
  height: 24px;
  line-height: 24px;
  width: 42px;
`;

export const Timeline = () => {
  const { getState, subscribe, useStoreState } = usePlayerStore();
  const buffered = useStoreState.buffered();
  const device = useStoreState.device();
  const durationInSeconds = useStoreState.durationInSeconds();
  const isControlsVisible = useStoreState.isControlsVisible();
  const isLive = useStoreState.isLive();
  const isLiveEdge = useStoreState.isLiveEdge();
  const player = useStoreState.player();
  const uiSize = useStoreState.uiSize();
  const dvrWindow = useStoreState.dvrWindow();

  const setIsDraggingTimeline = useStoreState.setIsDraggingTimeline();

  const { language } = useEmbedParametersContext();

  const [hoverPosition, setHoverPosition] = useState<number | false>(false);
  const [timelinePosition, setTimelinePosition] = useState<number>(0);
  const [timeRemaining, setTimeRemaining] = useState<number>();
  const [timelineDuration, setTimelineDuration] = useState<number | undefined>(
    durationInSeconds
  );
  const [ariaValueText, setAriaValueText] = useState<string>(
    i18n("seek_slider", { language })
  );

  const seekSliderRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimelineDuration(durationInSeconds);
  }, [durationInSeconds]);

  useEffect(() => {
    const unsubscribe = subscribe(
      state => state.seekableEnd,
      seekableEnd => {
        if (isLive && seekableEnd) {
          const offsettedDvrWindow = resolveDvrWindow(getState().dvrWindow);
          if (offsettedDvrWindow) {
            setTimelineDuration(
              seekableEnd < offsettedDvrWindow
                ? seekableEnd
                : offsettedDvrWindow
            );
          } else {
            setTimelineDuration(seekableEnd);
          }
        }
      },
      {
        fireImmediately: true
      }
    );

    return () => {
      unsubscribe();
    };
  }, [getState, isLive, subscribe]);

  useEffect(() => {
    if (timelineDuration) {
      setTimeRemaining(Math.max(timelineDuration - timelinePosition, 0));
    }
  }, [timelineDuration, timelinePosition]);

  const calculateEventPosition = (event: MouseEvent | React.Touch) => {
    if (seekSliderRef.current) {
      const rect = seekSliderRef.current.getBoundingClientRect();
      return (
        (Math.max(Math.min(event.clientX, rect.left + rect.width), rect.left) -
          rect.left) /
        rect.width
      );
    }
  };

  const isTouchEvent = (
    event: React.TouchEvent | React.MouseEvent
  ): event is React.TouchEvent => event.nativeEvent instanceof TouchEvent;

  const isMouseEvent = (
    event: React.TouchEvent | React.MouseEvent
  ): event is React.MouseEvent => event.nativeEvent instanceof MouseEvent;

  const calculatePosition = (event: React.MouseEvent | React.TouchEvent) => {
    if (seekSliderRef.current) {
      if (isMouseEvent(event)) {
        return calculateEventPosition(event.nativeEvent);
      } else if (isTouchEvent(event)) {
        return calculateEventPosition(event.nativeEvent.changedTouches[0]);
      }
    }
  };

  const onSeekEnd = (event: React.MouseEvent | React.TouchEvent) => {
    const seekableEnd = getState().seekableEnd;
    const liveOffsetOnSeekEnd =
      isLive && seekableEnd && timelineDuration
        ? seekableEnd - timelineDuration
        : 0;

    if (timelineDuration && seekSliderRef.current) {
      const value =
        timelineDuration * (calculatePosition(event) ?? 0) +
        liveOffsetOnSeekEnd;
      if (!isNaN(value)) {
        player.seekTo(value);
      } else {
        const value2 = parseInt((event.target as HTMLInputElement).value, 10);
        player.seekTo(value2);
      }
    }
  };

  useEffect(() => {
    const unsubscribe = subscribe(
      state => [state.seekableEnd, state.currentTime],
      stateValues => {
        const seekableEnd = stateValues[0];
        const currentTime = stateValues[1];
        if (!getState().isDraggingTimeline && currentTime !== undefined) {
          const offsettedDvrWindow = resolveDvrWindow(getState().dvrWindow);
          if (offsettedDvrWindow && seekableEnd && timelineDuration) {
            const offsetTime = currentTime - (seekableEnd - timelineDuration);
            if (offsetTime < 0) {
              setTimelinePosition(0);
            } else {
              setTimelinePosition(
                offsetTime > offsettedDvrWindow - LIVE_EDGE_THRESHOLD_IN_SECONDS
                  ? offsettedDvrWindow
                  : offsetTime
              );
            }
          } else {
            setTimelinePosition(currentTime);
          }
        }
      },
      {
        equalityFn: shallow,
        fireImmediately: true
      }
    );

    return () => {
      unsubscribe();
    };
  }, [getState, timelineDuration, subscribe]);

  const timelineElapsedColor = isLive
    ? progressBarLiveElapsedColor
    : progressBarElapsedColor;

  const seekToLiveEdge = () => {
    const seekableEnd = getState().seekableEnd;
    if (timelineDuration !== undefined && seekableEnd !== undefined) {
      setTimelinePosition(timelineDuration);
      player.seekTo(seekableEnd);
    }
  };

  useEffect(() => {
    if (isControlsVisible) {
      setAriaValueText(
        getTimelineAriaValueText({
          isLive,
          isLiveEdge,
          language,
          timelinePosition,
          timeRemaining
        })
      );
    }
  }, [
    isControlsVisible,
    isLive,
    isLiveEdge,
    language,
    timeRemaining,
    timelinePosition
  ]);

  const onInput = useCallback(
    event => {
      if (getState().isDraggingTimeline) {
        setTimelinePosition(parseInt(event.target.value, 10));
      }
    },
    [getState]
  );

  return (
    <TimelineContainer>
      {!device.isMobile && !isLive && (
        <Time aria-hidden={true} uiSize={uiSize}>
          {secondsToHHMMSSFormat(Math.max(timelinePosition ?? 0, 0))}
        </Time>
      )}
      <Progress
        isMobile={device.isMobile}
        visibility={
          isLive && !resolveDvrWindow(dvrWindow) ? "hidden" : "visible"
        }
        zoomOnHover>
        <Wrapper zoomOnHover>
          <BufferProgress
            aria-hidden={true}
            value={buffered}
            max={timelineDuration}
          />
        </Wrapper>
        <Wrapper zoomOnHover>
          <HoverProgress
            aria-hidden={true}
            value={hoverPosition ? hoverPosition * 100 : 0}
            max={100}
          />
        </Wrapper>
        <Wrapper zoomOnHover>
          <ElapsedProgress
            aria-hidden={true}
            backgroundColor={timelineElapsedColor}
            value={timelinePosition}
            max={timelineDuration}
          />
        </Wrapper>
        <SeekSlider
          aria-valuetext={ariaValueText}
          background={timelineElapsedColor}
          isDesktopVideo={!device.isMobile}
          max={timelineDuration}
          onInput={onInput}
          onMouseLeave={() => {
            setHoverPosition(false);
          }}
          onMouseMove={(event: React.MouseEvent) => {
            const position = calculatePosition(event);
            if (position) {
              setHoverPosition(position);
            }
          }}
          onMouseDown={() => {
            setIsDraggingTimeline(true);
          }}
          onMouseUp={event => {
            setIsDraggingTimeline(false);
            onSeekEnd(event);
          }}
          onTouchEnd={event => {
            setIsDraggingTimeline(false);
            setHoverPosition(false);
            onSeekEnd(event);
          }}
          onTouchMove={event => {
            setHoverPosition(
              +(event.target as HTMLInputElement).value /
                (timelineDuration as number)
            );
          }}
          onTouchStart={event => {
            setIsDraggingTimeline(true);
            (event.target as HTMLInputElement).focus();
          }}
          ref={seekSliderRef}
          value={timelinePosition}
        />
        <SeekTooltip
          duration={timelineDuration}
          hoverPosition={hoverPosition}
          seekSlider={seekSliderRef.current}
        />
      </Progress>
      {isLive ? (
        <LiveButton
          isLiveEdge={isLiveEdge}
          onClick={seekToLiveEdge}
          uiSize={uiSize}>
          LIVE
        </LiveButton>
      ) : (
        <Time aria-hidden={true} uiSize={uiSize}>
          -{secondsToHHMMSSFormat(timeRemaining ?? 0)}
        </Time>
      )}
    </TimelineContainer>
  );
};
